// Central API client — the ONLY place session errors are interpreted.
//
// Rules (see AuthContext for the state machine that consumes the events):
//   401  → attempt ONE silent refresh (single-flight, shared by every concurrent 401),
//          retry the original request ONCE with the renewed cookie. If the refresh itself
//          fails, emit ONE `auth:logout` event; AuthContext flips to `unauthenticated`
//          and <Protected> renders the login route (React navigation — never a hard
//          window.location redirect, so nothing can loop).
//   403  → never touches the session. The caller shows Access Denied / a toast.
//   4xx/5xx/timeout/offline → never touches the session. Caller-level error handling only.
// Auth uses httpOnly cookies set by the server — no tokens in JS-accessible storage.
import axios from 'axios';
import logger from './logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEV = process.env.NODE_ENV !== 'production';

export const AUTH_LOGOUT_EVENT = 'auth:logout';
const REFRESH_PATH = '/auth/refresh';
// Endpoints that must never trigger a refresh attempt (their 401 IS the answer).
const NO_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/portal/login'];

export const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

// Dev-only diagnostics. Never logs headers, cookies or tokens — only path + status.
export const authDebug = (...args) => { if (DEV) logger.debug('[auth]', ...args); };

const pathOf = (config) => {
  try { return new URL(config.url, config.baseURL || BACKEND_URL).pathname.replace(/^.*\/api/, ''); } catch { return config.url || ''; }
};

let refreshPromise = null;   // single-flight: at most one /auth/refresh in flight
let loggedOut = false;       // exactly-one logout per session loss (reset on next login)

/** Called by AuthContext after a successful login / session hydration. */
export const markAuthenticated = () => { loggedOut = false; };

/** Fire the ONE logout signal. AuthContext owns the state + navigation. */
export const signalLogout = (reason, source = 'api-client') => {
  if (loggedOut) return;
  loggedOut = true;
  authDebug('logout signalled', { reason, source });
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT, { detail: { reason, source } }));
};

/** Shared refresh. Resolves true on success, false on failure — never throws. */
export const refreshAccessToken = () => {
  if (!refreshPromise) {
    authDebug('refresh started');
    refreshPromise = api.post(REFRESH_PATH, null, { _skipAuth: true })
      .then(() => { authDebug('refresh successful'); return true; })
      .catch((e) => { authDebug('refresh failed', e?.response?.status ?? 'network'); return false; })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err.response?.status;
    const config = err.config || {};
    const path = pathOf(config);
    if (status) authDebug(`${(config.method || 'get').toUpperCase()} ${path} → ${status}`);
    else authDebug(`${(config.method || 'get').toUpperCase()} ${path} → network/timeout`);

    // Only 401 concerns the session. 403 = authenticated but not permitted → caller handles.
    if (status !== 401) return Promise.reject(err);
    if (config._skipAuth || config._retry || NO_REFRESH_PATHS.some((p) => path.startsWith(p))) {
      return Promise.reject(err);
    }
    // Portal (client) pages run their own session logic.
    if (window.location.pathname.startsWith('/portal')) return Promise.reject(err);

    authDebug('401 received', path);
    const renewed = await refreshAccessToken();
    if (renewed) {
      config._retry = true;
      return api(config);                          // retried exactly once with the fresh cookie
    }
    signalLogout('refresh_failed', path);
    return Promise.reject(err);
  },
);

export default api;
